import React, { useState, useRef } from "react";
import {
  Upload, Wand2, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft, Sparkles,
  ListChecks, History, Undo2, FileSpreadsheet,
} from "lucide-react";
import api from "../lib/api";
import { Modal, Button, Select, Badge } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum } from "../lib/constants";
import { useT } from "../lib/i18n";

const FIELDS = [
  { key: "name", label: "Menu Name", required: true },
  { key: "category", label: "Category" },
  { key: "code", label: "Menu Code" },
  { key: "price", label: "Selling Price" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
];

const Stepper = ({ step, t }) => {
  const steps = ["Upload", "Map Columns", "Preview", "Done"];
  const idx = { upload: 0, map: 1, preview: 2, summary: 3 }[step] ?? 0;
  return (
    <div className="mb-5 flex items-center gap-1" data-testid="menu-wizard-stepper">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${i <= idx ? "bg-primary text-white" : "bg-stone-100 text-ink-mute"}`}>
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/30 text-[10px]">{i + 1}</span>
            <span className="hidden sm:inline">{t(s)}</span>
          </div>
          {i < steps.length - 1 && <span className={`h-0.5 w-3 ${i < idx ? "bg-primary" : "bg-stone-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function MenuImportWizard({ open, onClose, onDone }) {
  const { t } = useT();
  const toast = useToast();
  const fileRef = useRef();
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detect, setDetect] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [dupMode, setDupMode] = useState("skip");
  const [summary, setSummary] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  const reset = () => {
    setStep("upload"); setFile(null); setDetect(null); setMapping({}); setPreview(null);
    setSummary(null); setDupMode("skip"); setShowErrors(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const close = () => { reset(); onClose(); };

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/menu-import/detect", fd);
      setDetect(data);
      setMapping({ ...(data.suggested_mapping || {}) });
      setStep("map");
    } catch (err) { toast.error(err.response?.data?.detail || t("Could not read the file")); }
    finally { setBusy(false); }
  };

  const goPreview = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post("/menu-import/preview", fd);
      setPreview(data); setStep("preview");
    } catch (err) { toast.error(err.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/menu-import/confirm", {
        rows: preview.rows, duplicate_mode: dupMode, file_name: file?.name,
      });
      setSummary(data); setStep("summary"); onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  let footer = null;
  if (step === "summary") footer = <Button onClick={close} data-testid="menu-wizard-done">{t("Done")}</Button>;
  else if (step === "map") footer = <><Button variant="outline" onClick={() => { setStep("upload"); setDetect(null); }} data-testid="menu-back-upload"><ArrowLeft size={16} /> {t("Back")}</Button>
    <Button onClick={goPreview} loading={busy} disabled={!mapping.name} data-testid="menu-next-preview">{t("Next: Preview")} <ArrowRight size={16} /></Button></>;
  else if (step === "preview") footer = <><Button variant="outline" onClick={() => setStep("map")} data-testid="menu-back-map"><ArrowLeft size={16} /> {t("Back")}</Button>
    <Button onClick={doImport} loading={busy} data-testid="menu-import-confirm">{t("Import")} {preview?.new} {t("new")}</Button></>;
  else footer = <Button variant="outline" onClick={close}>{t("Close")}</Button>;

  return (
    <Modal open={open} onClose={close} size="lg" title={t("Menu Import Wizard")} footer={footer}>
      {step !== "summary" && <Stepper step={step} t={t} />}

      {step === "upload" && (
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">{t("Bulk-create menu master records from a POS export. Creates menus only — no recipes, BOM, inventory, or sales.")}</p>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-line py-10 text-ink-soft hover:bg-stone-50" data-testid="menu-dropzone">
            <Upload size={28} className="text-primary" />
            <span className="text-sm font-semibold">{busy ? t("Reading file…") : t("Choose CSV / XLS / XLSX file")}</span>
            <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx,.xlsm" onChange={onFile} className="hidden" data-testid="menu-file-input" />
          </label>
        </div>
      )}

      {step === "map" && detect && (
        <div className="space-y-4">
          <div className="rounded-xl bg-stone-50 px-3 py-2 text-xs" data-testid="menu-diagnostics">
            <span className="font-semibold text-ink-soft">{t("Detected")}: </span>
            <span className="text-ink-mute">{detect.total} {t("rows")} · {(detect.headers || []).length} {t("columns")}</span>
          </div>
          {(detect.headers || []).length === 0 ? (
            <div className="flex items-center gap-2 rounded-xl border border-danger/30 bg-danger-light/40 p-3 text-sm font-semibold text-danger-text" data-testid="menu-no-headers">
              <AlertTriangle size={16} /> {t("No column headers detected")}
            </div>
          ) : (
            <>
              <p className="text-sm text-ink-soft">{t("Map each Central Flow field to a column from your file.")} <span className="text-ink-mute">({detect.total} {t("rows detected")})</span></p>
              <div className="space-y-2.5">
                {FIELDS.map((f) => {
                  const col = mapping[f.key];
                  const sample = col ? (detect.sample_rows || []).map((r) => r[col]).find((v) => v != null && String(v).trim() !== "") : null;
                  return (
                    <div key={f.key} className="grid grid-cols-2 items-start gap-3" data-testid={`menu-map-row-${f.key}`}>
                      <div className="flex items-center gap-2 pt-2 text-sm font-semibold text-ink">
                        {t(f.label)}
                        {f.required ? <Badge tone="danger">{t("Mandatory")}</Badge> : <Badge tone="mute">{t("Optional")}</Badge>}
                      </div>
                      <div>
                        <Select value={mapping[f.key] || ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })} data-testid={`menu-map-select-${f.key}`}>
                          <option value="">{f.required ? t("Choose a column…") : t("— none —")}</option>
                          {detect.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                        </Select>
                        {sample != null && (
                          <p className="mt-1 truncate text-[11px] text-ink-mute" title={String(sample)}>
                            {t("Sample")}: <span className="font-medium text-ink-soft">{String(sample)}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {step === "preview" && preview && (
        <div>
          <div className="mb-3 grid grid-cols-4 gap-2" data-testid="menu-preview-stats">
            <Stat label={t("Total Rows")} value={fmtNum(preview.total)} tone="info" testid="menu-stat-total" />
            <Stat label={t("New Menus")} value={fmtNum(preview.new)} tone="success" testid="menu-stat-new" />
            <Stat label={t("Existing")} value={fmtNum(preview.duplicate)} tone="warn" testid="menu-stat-existing" />
            <Stat label={t("Invalid")} value={fmtNum(preview.invalid)} tone="danger" testid="menu-stat-invalid" />
          </div>

          {preview.duplicate > 0 && (
            <div className="mb-3 rounded-xl border border-line p-3" data-testid="menu-dup-mode">
              <p className="mb-2 text-sm font-semibold text-ink">{t("Existing menus (matched by code/name):")}</p>
              <div className="flex gap-2">
                <button onClick={() => setDupMode("skip")} data-testid="menu-dup-skip"
                  className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold ${dupMode === "skip" ? "border-primary bg-primary-light/40" : "border-line text-ink-soft"}`}>{t("Skip Existing")}</button>
                <button onClick={() => setDupMode("update")} data-testid="menu-dup-update"
                  className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold ${dupMode === "update" ? "border-primary bg-primary-light/40" : "border-line text-ink-soft"}`}>{t("Update Existing")}</button>
              </div>
            </div>
          )}
          {preview.name_warnings > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="menu-name-warning">
              <AlertTriangle size={14} /> {preview.name_warnings} {t("menu(s) match an existing name")}
            </div>
          )}
          {preview.invalid > 0 && (
            <button onClick={() => setShowErrors((s) => !s)} className="mb-2 text-xs font-semibold text-primary" data-testid="menu-toggle-errors"><ListChecks size={12} className="inline" /> {showErrors ? t("Hide errors") : t("View errors")}</button>
          )}
          {showErrors && (
            <div className="mb-3 max-h-28 overflow-auto rounded-xl border border-danger/30 bg-danger-light/30 p-2 text-xs" data-testid="menu-error-list">
              {preview.errors.map((e, i) => <div key={i}><b>{t("Row")} {e.row}:</b> <span className="text-danger-text">{e.error}</span></div>)}
            </div>
          )}
          <p className="mb-1 text-[11px] text-ink-mute">{t("Showing first 20 rows")}</p>
          <div className="max-h-72 overflow-auto rounded-xl border border-line">
            <table className="w-full text-xs" data-testid="menu-preview-table">
              <thead className="sticky top-0 bg-stone-50"><tr className="text-left text-ink-mute">
                <th className="px-3 py-2">{t("Menu Name")}</th><th className="px-3 py-2">{t("Category")}</th><th className="px-3 py-2">{t("Code")}</th>
                <th className="px-3 py-2 text-right">{t("Price")}</th><th className="px-3 py-2">{t("Status")}</th></tr></thead>
              <tbody>
                {preview.preview_rows.map((r, i) => (
                  <tr key={i} className={`border-t border-line/60 ${r.status === "INVALID" ? "bg-danger-light/30" : ""}`}>
                    <td className="px-3 py-1.5 font-medium text-ink">{r.name || "—"}</td>
                    <td className="px-3 py-1.5">{r.category || "—"}</td>
                    <td className="px-3 py-1.5">{r.code || "—"}</td>
                    <td className="px-3 py-1.5 text-right">{r.price ? fmtRp(r.price) : "—"}</td>
                    <td className="px-3 py-1.5">
                      {r.status === "NEW" && <Badge tone="success">{t("New")}</Badge>}
                      {r.status === "DUPLICATE" && <Badge tone="warn">{t("Existing")}</Badge>}
                      {r.status === "INVALID" && <span className="text-danger-text">{r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === "summary" && summary && (
        <div className="space-y-4 text-center" data-testid="menu-import-summary">
          <div className="flex flex-col items-center gap-2 py-2">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-light text-success-text"><CheckCircle2 size={30} /></span>
            <h3 className="font-heading text-lg font-extrabold text-ink">{t("Import Complete")}</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t("Imported")} value={fmtNum(summary.imported)} tone="success" testid="menu-summary-imported" />
            <Stat label={t("Updated")} value={fmtNum(summary.updated)} tone="info" testid="menu-summary-updated" />
            <Stat label={t("Skipped")} value={fmtNum(summary.skipped)} tone="mute" testid="menu-summary-skipped" />
            <Stat label={t("Errors")} value={fmtNum(summary.errors)} tone={summary.errors ? "danger" : "mute"} testid="menu-summary-errors" />
          </div>
        </div>
      )}
    </Modal>
  );
}

export function MenuImportHistory({ open, onClose, onChanged }) {
  const { t } = useT();
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const load = React.useCallback(() => {
    setRows(null);
    api.get("/menu-import/history").then(({ data }) => setRows(data.history || [])).catch(() => setRows([]));
  }, []);
  React.useEffect(() => { if (open) load(); }, [open, load]);

  const rollback = async (id) => {
    if (!window.confirm(t("Roll back this import? Created menus will be removed and updates reverted."))) return;
    try {
      const { data } = await api.post(`/menu-import/history/${id}/rollback`);
      toast.success(`${t("Rolled back")}: ${data.removed} ${t("removed")}, ${data.restored} ${t("restored")}`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" title={t("Menu Import History")}>
      {rows === null ? <div className="py-10 text-center text-sm text-ink-mute">{t("Loading…")}</div>
        : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-ink-mute" data-testid="menu-history-empty"><FileSpreadsheet size={28} /><p className="text-sm">{t("No import history yet")}</p></div>
        ) : (
          <div className="max-h-[60vh] overflow-auto rounded-xl border border-line">
            <table className="w-full text-sm" data-testid="menu-history-table">
              <thead className="sticky top-0 bg-stone-50"><tr className="text-left text-xs uppercase text-ink-mute">
                <th className="px-3 py-2">{t("When")}</th><th className="px-3 py-2">{t("File")}</th>
                <th className="px-3 py-2 text-right">{t("Imported")}</th><th className="px-3 py-2 text-right">{t("Updated")}</th>
                <th className="px-3 py-2">{t("By")}</th><th className="px-3 py-2"></th></tr></thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.id} className="border-t border-line/60" data-testid={`menu-history-row-${h.id}`}>
                    <td className="px-3 py-2 text-ink-soft">{(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2">{h.file_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold text-success-text">{fmtNum(h.imported)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(h.updated)}</td>
                    <td className="px-3 py-2 text-ink-mute">{h.created_by_name}</td>
                    <td className="px-3 py-2 text-right">
                      {h.rolled_back ? <Badge tone="mute">{t("Rolled back")}</Badge>
                        : <Button variant="outline" onClick={() => rollback(h.id)} data-testid={`menu-rollback-${h.id}`}><Undo2 size={14} /> {t("Rollback")}</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Modal>
  );
}

function Stat({ label, value, tone = "primary", testid }) {
  const tones = { primary: "text-primary", success: "text-success-text", info: "text-blue-600", danger: "text-danger-text", warn: "text-amber-600", mute: "text-ink-soft" };
  return (
    <div className="rounded-xl bg-stone-50 p-3 text-center" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase text-ink-mute">{label}</p>
      <p className={`text-base font-extrabold ${tones[tone]}`}>{value}</p>
    </div>
  );
}
